"use client"

import { useState, useEffect, useCallback } from "react"
import { cityService } from "@/services/api"
import type { City, CityResDTO } from "@/types/api"

interface UseCitiesReturn {
  cities: City[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  clearError: () => void
}

// Transform API city to frontend city
function transformApiCity(apiCity: CityResDTO): City {
  return {
    id: apiCity.id,
    name: apiCity.name,
  }
}

export function useCities(): UseCitiesReturn {
  const [cities, setCities] = useState<City[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCities = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await cityService.getCities()
      const transformedCities = response.map(transformApiCity)
      setCities(transformedCities)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al cargar las ciudades"
      setError(errorMessage)
      console.error("Error fetching cities:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  useEffect(() => {
    fetchCities()
  }, [fetchCities])

  return {
    cities,
    loading,
    error,
    refetch: fetchCities,
    clearError,
  }
}
